import type { DealAgeFields } from "../../lib/dealAge";
import { daysInStage, daysInPhase, daysSinceCreated } from "../../lib/dealAge";

/**
 * Standard age tracker for deal cards: "Nd in stage" + "Nd in phase" + "Nd since created".
 *
 * Convention: mount in the card's status corner / meta row (next to the stage
 * chip). Stage age is tone-coded (slate < warnAt, amber >= warnAt, rose >=
 * dangerAt) so aging deals pop; created age stays neutral slate.
 */
export default function DealAgeChips({
  deal,
  warnAt = 7,
  dangerAt = 14,
  align = "left",
  className = "",
}: {
  deal: DealAgeFields;
  /** Days in stage at which the stage chip turns amber. */
  warnAt?: number;
  /** Days in stage at which the stage chip turns rose. */
  dangerAt?: number;
  align?: "left" | "right";
  className?: string;
}) {
  const stageDays = daysInStage(deal);
  const phaseDays = daysInPhase(deal);
  const createdDays = daysSinceCreated(deal);

  const stageTone =
    stageDays >= dangerAt
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : stageDays >= warnAt
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-50 text-slate-500 border-slate-200";

  return (
    <div
      className={
        "flex flex-wrap items-center gap-1 " +
        (align === "right" ? "justify-end " : "") +
        className
      }
    >
      <span
        className={
          "inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap " +
          stageTone
        }
        title="Days in current stage"
      >
        ⏱ {stageDays}d in stage
      </span>
      {phaseDays != null && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-slate-50 text-slate-500 border-slate-200 whitespace-nowrap"
          title="Days in current phase"
        >
          {phaseDays}d in phase
        </span>
      )}
      {createdDays != null && (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-slate-50 text-slate-500 border-slate-200 whitespace-nowrap"
          title="Days since the deal was created"
        >
          {createdDays}d since created
        </span>
      )}
    </div>
  );
}
